<template>
  <div
    id="balance-due-registrations"
    class="container-fluid"
    v-if="loggedInUser">
    <!-- Payment Modal -->
    <b-modal
      id="payment-modal"
      ref="paymentModal"
      size="lg"
      title="Make A Payment"
      v-model="paymentModalOpen"
      @hidden="resetPayment"
      @hide="checkDirtyModalForm($event, paymentDirty)"
      hide-footer>
      <b-form
        id="basic-info-form"
        name="basicInfoForm">
        <div class="row">
          <!-- Registration -->
          <div class="col-lg">
            <label for="payment-amount">
              {{paymentInfo.event}}<br>

              <small
                v-if="paymentInfo.minPayment === paymentInfo.maxPayment"
                class="text-danger">
                You must pay the remaining balance.
              </small>

              <small
                v-else
                class="text-danger">
                Enter an amount between
                {{paymentInfo.minPayment | currency}}
                and
                {{paymentInfo.maxPayment | currency}}.
              </small>
            </label>
          </div>
          <!-- / Registration -->

          <!-- Payment -->
          <div class="col-lg-auto payment-entry">
            <Number
              placeholder="0.00"
              type="currency"
              step="10.00"
              id="payment-amount"
              name="paymentAmount"
              :min="paymentInfo.minPayment"
              :max="paymentInfo.maxPayment"
              :disabled="!!paymentLoadingMsg"
              :readonly="paymentInfo.minPayment === paymentInfo.maxPayment"
              v-model="payment.partial_payments[payment.apply_to_registration_id]"
            />
          </div>
        </div>

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <!-- Payment Request Button -->
          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <button
              id="stripe-payment-request-mount"
              class="stripe-payment-request w-100 w-lg-auto"
              v-if="paymentRequestStatus !== null"
              type="button"
              :disabled="!!paymentLoadingMsg">
            </button>
          </div>
          <!-- / Payment Request Button -->

          <!-- Stripe Checkout -->
          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              variant="success"
              type="button"
              :disabled="!!paymentLoadingMsg"
              @click="checkoutStart">
              Pay With Credit Card
            </b-button>
          </div>
          <!-- / Stripe Checkout -->

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="button"
              variant="danger"
              @click="$refs.paymentModal.hide();">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="payment-loading"
        variant="light"
        :msg="paymentLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- / Payment Modal -->

    <!-- Page Header -->
    <div class="row border-bottom border-muted py-3">
      <div class="
        col
        d-flex
        flex-column
        flex-lg-row
        align-items-center
      ">
        <h3 class="mb-3 m-lg-0">
          Registrations with Balance Due
        </h3>

        <div class="flex-fill d-xs-none d-lg-block text-center"></div>

        <div class="m-0 d-flex text-right">
          Total Balance Due: {{totalBalanceDue | currency}}
        </div>
      </div>
    </div>
    <!-- / Page Header -->

    <!-- Registrations -->
    <div class="mt-3">
      <div
        v-for="registration in balanceDueRegistrations"
        :key="registration.id">
        <div class="d-flex flex-column flex-lg-row align-items-lg-center">
          <!-- Registrant & Event -->
          <RegistrantAndEvent
            class="flex-fill text-lg-left text-center"
            :registration="registration"
          />
          <!-- / Registrant & Event -->

          <!-- Payment Summary -->
          <div class="mt-3 mt-lg-0 text-right mx-auto">
            <PaymentSummary
              class="d-flex justify-content-center justify-content-lg-end"
              :registration="registration"
            />

            <b-button
              class="mr-lg-0 ml-lg-auto mx-auto d-block mt-3"
              type="button"
              variant="primary"
              @click="openPayment(registration)">
              Make A Payment
            </b-button>

            <div
              class="mt-3 text-center text-lg-left"
              v-if="registration.balance_due > 0 && registration.waitlist">
              *Balance due when moved to Active Roster
            </div>

            <div
              class="mt-3 text-center text-lg-left"
              v-if="registration.balance_due > 0 && !registration.waitlist">
              *Balance due prior to start of event
            </div>
          </div>
          <!-- / Payment Summary -->
        </div>
        <hr>
      </div>
    </div>
    <!-- / Registrations -->

    <!-- No Balance Due Indicator -->
    <div
      id="no-balance-due"
      class="d-flex flex-column justify-content-center text-center p-3 font-italic"
      v-if="balanceDueRegistrations.length === 0">
      All of your registrations have been paid in full.
    </div>
    <!-- / No Balance Due Indicator -->

    <!-- Stripe Checkout -->
    <vue-stripe-checkout
      ref="checkoutRef"
      image="https://stripe.com/img/documentation/checkout/marketplace.png"
      name="KJK Registrations"
      :description="paymentInfo.event"
      :allow-remember-me="false"
      :amount="costInCents"
      @done="checkoutDone"
      @canceled="checkoutCanceled">
    </vue-stripe-checkout>
    <!-- / Stripe Checkout -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/mixin.app';
import Number from '../components/Number.vue';
import DirtyForm from '../services/dirty-form';

const defaultPayment = {
  apply_to_registration_id: '',
  partial_payments: {},
};

const defaultPaymentInfo = {
  maxPayment: 0,
  minPayment: 0,
  event: '',
  uuid: '',
};

// eslint-disable-next-line
let stripe;
let elements;
let paymentRequest;
let prButton;

export default {
  name: 'balance-due-registrations',
  mixins: [
    AppMixin,
    DirtyForm,
  ],
  components: {
    Number,
  },
  data() {
    return {
      // payment
      paymentModalOpen: false,
      payment: defaultPayment,
      paymentCopy: _.clone(defaultPayment),
      paymentInfo: _.clone(defaultPaymentInfo),
      paymentLoadingMsg: '',
      paymentRequestStatus: null,
      paymentProcessing: false,
      paymentRequestButtonMounted: false,
    };
  },
  watch: {
    costInCents(cost) {
      if (this.paymentModalOpen) {
        this.updatePaymentRequestAmount(cost);
      }
    },
  },
  computed: {
    totalBalanceDue() {
      return _.sumBy(this.balanceDueRegistrations, 'balance_due');
    },
    paymentDirty() {
      const paymentJSON = JSON.stringify(this.payment);
      const paymentCopyJSON = JSON.stringify(this.paymentCopy);
      return paymentJSON !== paymentCopyJSON;
    },
    costInCents() {
      return this.convertFloatDollarsToIntegerCents(
        this.payment.partial_payments[this.payment.apply_to_registration_id],
      );
    },
  },
  methods: {
    openPayment(registration) {
      this.payment = {
        apply_to_registration_id: registration.id,
        partial_payments: {
          [registration.id]: registration.balance_due,
        },
      };

      if (this.paymentRequestStatus !== null && !this.paymentRequestButtonMounted) {
        prButton.mount('#stripe-payment-request-mount');
        this.paymentRequestButtonMounted = true;
      }

      this.updatePaymentRequestAmount(this.costInCents);

      const max = registration.balance_due;
      const min = registration.calc_minimum_payment >= max
        ? max
        : registration.calc_minimum_payment;

      this.paymentCopy = _.clone(this.payment);
      this.paymentInfo.uuid = registration.registration_session_uuid;
      this.paymentInfo.minPayment = min;
      this.paymentInfo.maxPayment = max;
      this.paymentInfo.event = `${registration.event.name} - ${registration.registrant.full_name}`;
      this.paymentModalOpen = true;
    },
    resetPayment() {
      // reset payment
      this.payment = _.clone(defaultPayment);
      this.paymentCopy = _.clone(defaultPayment);

      // reset payment info
      this.paymentInfo = _.clone(defaultPaymentInfo);
    },
    checkoutStart() {
      this.paymentLoadingMsg = true;
      this.$nextTick(this.$refs.checkoutRef.open);
    },
    checkoutDone({ token }) {
      const payment = _.clone(this.payment);

      payment.stripe_source_token = token.id;

      const chargePromise = DataSvc.createPaymentCharge(this.paymentInfo.uuid, payment);

      chargePromise
        .finally(() => {
          this.paymentLoadingMsg = '';
        });

      return chargePromise
        .then(() => {
          this.paymentModalOpen = false;
          this.showGlobalLoader('Loading data...');
          return DataSvc.getClosedRegistrationSessions();
        })
        .then((closedRegistrations) => {
          this.closedRegistrations = closedRegistrations;
        })
        .finally(this.hideGlobalLoader);
    },
    checkoutCanceled() {
      this.paymentLoadingMsg = false;
      this.paymentProcessing = false;
    },
    updatePaymentRequestAmount(costInCents) {
      if (this.paymentRequestStatus !== null
        && !this.paymentProcessing) {
        paymentRequest.update({
          total: {
            label: 'KJK Registrations',
            amount: costInCents,
          },
        });
      }
    },
  },
  destroyed() {
    prButton.unmount();
    prButton.destroy();
  },
  created() {
    const initBalanceDueEvents = () => {
      // stripe init
      // eslint-disable-next-line
      stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_API_KEY);
      elements = stripe.elements();

      // create payment request instance with current cost
      paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'KJK Registrations',
          amount: 0,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // mount payment request button if possible
      paymentRequest.canMakePayment()
        .then((status) => {
          this.paymentRequestStatus = status;

          // if enabled listen to events & create button but mount later
          if (status !== null) {
            // listen for payment request complete and create charge for token
            paymentRequest.on('token', (e) => {
              this.paymentLoadingMsg = 'Processing payment...';
              this.paymentProcessing = true;

              // complete the charge
              this.checkoutDone(e)
                .then(() => e.complete('success'), () => e.complete('fail'))
                .finally(this.checkoutCanceled);
            });

            // listen for payment request canceled event
            paymentRequest.on('cancel', this.checkoutCanceled);

            try {
              // create payment request button stripe element
              prButton = elements.create('paymentRequestButton', {
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    height: '38px',
                  },
                },
              });
            } catch (e) {
              if (e.message !== 'Can only create one Element of type paymentRequestButton.') {
                // eslint-disable-next-line
                console.error(e);
              }
            }

            prButton.on('click', () => {
              this.paymentLoadingMsg = true;
            });
          }
        });
    };

    this.initPromise.then(initBalanceDueEvents);
  },
};
</script>

<style lang="scss">
#balance-due-registrations {
  #no-balance-due {
    position: fixed;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
